import { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Layers, Sparkles, CheckCircle2, ChevronRight, Lock, GraduationCap,
  Users, ShoppingCart, Wallet, FileText, Building2, Megaphone, Bot,
  Zap, BarChart3, Award, FileSignature, Globe2, Mail, PhoneCall,
} from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";
import HelpIcon from "./help/HelpIcon";

/**
 * Liste centrale des labels + icones + categories des modules.
 * Ne contient que des modules visibles cote tenant (pas les umbrella legacy).
 */
const MODULES_META = {
  // Mon Espace
  espace_taches: { label: "Mes Taches", icon: CheckCircle2, category: "Mon Espace" },
  espace_equipement: { label: "Mon Equipement", icon: Layers, category: "Mon Espace" },
  espace_depenses: { label: "Mes Depenses", icon: Wallet, category: "Mon Espace" },
  espace_documents: { label: "Mes Documents", icon: FileText, category: "Mon Espace" },
  messages: { label: "Messagerie", icon: Mail, category: "Mon Espace" },
  agenda: { label: "Agenda", icon: Layers, category: "Mon Espace" },
  // Ventes & CRM
  clients: { label: "Clients", icon: Users, category: "Ventes & CRM" },
  prospects: { label: "Prospects", icon: ShoppingCart, category: "Ventes & CRM" },
  crm: { label: "CRM", icon: Users, category: "Ventes & CRM" },
  facturation: { label: "Facturation", icon: FileText, category: "Ventes & CRM" },
  pilotage: { label: "Pilotage", icon: BarChart3, category: "Ventes & CRM" },
  yousign: { label: "Signature Yousign", icon: FileSignature, category: "Ventes & CRM" },
  intel_agent: { label: "Enquêteur IA", icon: Sparkles, category: "Ventes & CRM" },
  // Vente Augmentée IA
  aircall_translator: { label: "Traducteur d'appels", icon: PhoneCall, category: "Vente Augmentée IA" },
  commercial_panel: { label: "Mon panel commercial", icon: Layers, category: "Vente Augmentée IA" },
  objection_cookbook: { label: "Cookbook objections", icon: Sparkles, category: "Vente Augmentée IA" },
  coach_stats: { label: "Stats Coach IA", icon: BarChart3, category: "Vente Augmentée IA" },
  weekly_digest: { label: "Copilote manager hebdo", icon: Bot, category: "Vente Augmentée IA" },
  // Commandes & Stocks
  inventory: { label: "Commandes & Stocks", icon: ShoppingCart, category: "Commandes & Stocks" },
  // Marketing
  marketing_social: { label: "Reseaux sociaux", icon: Megaphone, category: "Marketing" },
  marketing_mailing: { label: "Mailing", icon: Mail, category: "Marketing" },
  marketing_google: { label: "Google Marketing", icon: Globe2, category: "Marketing" },
  workflows: { label: "Workflows & Automations", icon: Zap, category: "Marketing" },
  messaging_meta: { label: "Messagerie Meta", icon: Mail, category: "Marketing" },
  briefs_hebdo: { label: "Briefs hebdo", icon: Megaphone, category: "Marketing" },
  affiliate: { label: "Affiliation", icon: Users, category: "Marketing" },
  // Formation
  formation_bibliotheque: { label: "Bibliotheque & programmes", icon: GraduationCap, category: "Formation" },
  formation_lms: { label: "Sessions & LMS", icon: GraduationCap, category: "Formation" },
  formation_inscriptions: { label: "Pilotage inscriptions", icon: Users, category: "Formation" },
  formation_bpf: { label: "Stats BPF", icon: BarChart3, category: "Formation" },
  formation_conformite: { label: "Conformite Qualiopi", icon: CheckCircle2, category: "Formation" },
  formation_evaluations: { label: "Evaluations", icon: Award, category: "Formation" },
  formation_catalogue: { label: "Catalogue public", icon: Globe2, category: "Formation" },
  formation_edof: { label: "Export EDOF", icon: FileText, category: "Formation" },
  formation_certifications: { label: "Certifications RS/RNCP", icon: Award, category: "Formation" },
  qualiopi: { label: "Qualiopi", icon: CheckCircle2, category: "Formation" },
  welcome_stagiaire: { label: "Welcome Stagiaire J-7", icon: Mail, category: "Formation" },
  // Comptabilité & Finances
  depenses: { label: "Depenses", icon: Wallet, category: "Comptabilité & Finances" },
  encaissements: { label: "Encaissements", icon: Wallet, category: "Comptabilité & Finances" },
  tresorerie: { label: "Tresorerie", icon: BarChart3, category: "Comptabilité & Finances" },
  ppf_factur_x: { label: "PPF Factur-X (PDF/A-3)", icon: FileSignature, category: "Comptabilité & Finances" },
  e_invoices: { label: "Pilotage e-invoices", icon: FileText, category: "Comptabilité & Finances" },
  // Multi-Sociétés
  societes: { label: "Gestion Societes", icon: Building2, category: "Multi-Sociétés" },
  holding: { label: "Holding", icon: Building2, category: "Multi-Sociétés" },
  analytics: { label: "Analytics & Reporting", icon: BarChart3, category: "Multi-Sociétés" },
  // Agents IA & Automatisation
  agent_ia: { label: "Agent IA", icon: Bot, category: "Agents IA & Automatisation" },
  // Administration
  builder: { label: "Builder", icon: Layers, category: "Administration" },
  outils: { label: "Equipements", icon: Layers, category: "Administration" },
  documents: { label: "Documents", icon: FileText, category: "Administration" },
  equipe: { label: "Equipe", icon: Users, category: "Administration" },
  time_tracking: { label: "Pointage GPS/WhatsApp", icon: CheckCircle2, category: "Administration" },
  integration_resend: { label: "Integration Email", icon: Mail, category: "Administration" },
  integration_aircall: { label: "Aircall (telephonie)", icon: PhoneCall, category: "Administration" },
  integration_branding: { label: "Marque blanche", icon: Sparkles, category: "Administration" },
};

const CATEGORY_ORDER = [
  "Mon Espace", "Ventes & CRM", "Vente Augmentée IA", "Commandes & Stocks",
  "Marketing", "Formation", "Comptabilité & Finances", "Multi-Sociétés",
  "Agents IA & Automatisation", "Administration",
];

const CATEGORY_COLORS = {
  "Mon Espace": "from-blue-500/10 to-blue-500/5 border-blue-500/20 text-blue-300",
  "Ventes & CRM": "from-purple-500/10 to-purple-500/5 border-purple-500/20 text-purple-300",
  "Vente Augmentée IA": "from-fuchsia-500/10 to-fuchsia-500/5 border-fuchsia-500/20 text-fuchsia-300",
  "Commandes & Stocks": "from-teal-500/10 to-teal-500/5 border-teal-500/20 text-teal-300",
  "Marketing": "from-pink-500/10 to-pink-500/5 border-pink-500/20 text-pink-300",
  "Formation": "from-amber-500/10 to-amber-500/5 border-amber-500/20 text-amber-300",
  "Comptabilité & Finances": "from-emerald-500/10 to-emerald-500/5 border-emerald-500/20 text-emerald-300",
  "Multi-Sociétés": "from-indigo-500/10 to-indigo-500/5 border-indigo-500/20 text-indigo-300",
  "Agents IA & Automatisation": "from-cyan-500/10 to-cyan-500/5 border-cyan-500/20 text-cyan-300",
  "Administration": "from-slate-500/10 to-slate-500/5 border-slate-500/20 text-slate-300",
};

/**
 * Widget recapitulatif des modules activés du tenant courant.
 * Groupé par catégorie avec couleurs distinctes, compteurs, et liste des addons disponibles.
 */
export default function MyModulesWidget() {
  const { user, axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);

  const tenant = user?.tenant;
  const activeModules = useMemo(() => tenant?.active_modules || [], [tenant]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/billing/my/upgrade-options");
      setAddons(r.data?.available_addons || []);
    } catch (e) {
      // 403 = pas tenant_admin, OK
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const modulesByCategory = useMemo(() => {
    const grouped = {};
    activeModules.forEach(modKey => {
      const meta = MODULES_META[modKey];
      if (!meta) return; // skip umbrella legacy / unknown
      const cat = meta.category;
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push({ key: modKey, ...meta });
    });
    return grouped;
  }, [activeModules]);

  const total = activeModules.filter(m => MODULES_META[m]).length;
  const categoriesCount = Object.keys(modulesByCategory).length;

  if (loading) {
    return (
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-6 mb-6" data-testid="my-modules-widget-loading">
        <Skeleton className="h-5 w-48 mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-[#14101c] to-[#0f0b17] border border-white/5 rounded-xl overflow-hidden mb-6" data-testid="my-modules-widget">
      {/* Header */}
      <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#f7c935]/10 border border-[#f7c935]/20 flex items-center justify-center">
            <Layers className="w-4 h-4 text-[#f7c935]" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white flex items-center gap-1.5" data-testid="modules-widget-title">
              Mes modules activés
              <HelpIcon topic="modules_overview" />
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              <span className="text-[#f7c935] font-bold" data-testid="modules-count-total">{total}</span> modules actifs ·
              <span className="ml-1">{categoriesCount} catégories</span>
              {tenant?.plan && <span className="ml-2 px-1.5 py-0.5 rounded bg-white/5 text-[10px]">{tenant.plan}</span>}
            </p>
          </div>
        </div>
        {addons.length > 0 && (
          <div className="hidden md:flex items-center gap-1.5 text-[10px] text-slate-500" data-testid="modules-addons-available">
            <Sparkles className="w-3 h-3 text-[#f7c935]" />
            <span><span className="text-[#f7c935] font-bold">{addons.length}</span> addons disponibles</span>
          </div>
        )}
      </div>

      {/* Modules grouped by category */}
      <div className="p-6 space-y-5">
        {CATEGORY_ORDER.filter(cat => modulesByCategory[cat]).map(cat => {
          const colorClass = CATEGORY_COLORS[cat] || "from-slate-500/10 to-slate-500/5 border-slate-500/20 text-slate-300";
          const mods = modulesByCategory[cat];
          return (
            <div key={cat} data-testid={`category-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-center gap-2 mb-2.5">
                <h4 className={`text-[10px] font-bold uppercase tracking-wider ${colorClass.split(" ").pop()}`}>{cat}</h4>
                <span className="text-[10px] text-slate-600">·</span>
                <span className="text-[10px] text-slate-500">{mods.length} module{mods.length > 1 ? "s" : ""}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {mods.map(m => {
                  const Icon = m.icon || Layers;
                  return (
                    <div
                      key={m.key}
                      className={`group relative rounded-lg p-3 bg-gradient-to-br ${colorClass} border transition-all hover:scale-[1.02] hover:shadow-lg cursor-default`}
                      data-testid={`module-card-${m.key}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <Icon className="w-4 h-4 mt-0.5 flex-shrink-0 opacity-90" />
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-semibold text-white truncate">{m.label}</div>
                          <div className="text-[9px] text-slate-500 mt-0.5 font-mono">{m.key}</div>
                        </div>
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400/80 flex-shrink-0 opacity-80 group-hover:opacity-100" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {total === 0 && (
          <div className="text-center py-8" data-testid="modules-empty-state">
            <Lock className="w-8 h-8 text-slate-600 mx-auto mb-2" />
            <p className="text-xs text-slate-500">Aucun module activé. Souscrivez à une offre pour commencer.</p>
          </div>
        )}
      </div>

      {/* Available addons CTA */}
      {addons.length > 0 && (
        <div className="px-6 py-4 border-t border-white/5 bg-[#0b0811]/50" data-testid="modules-addons-cta">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <Sparkles className="w-4 h-4 text-[#f7c935]" />
              <div>
                <div className="text-xs font-semibold text-white">Découvrez des modules complémentaires</div>
                <div className="text-[10px] text-slate-500">{addons.length} addon{addons.length > 1 ? "s" : ""} disponible{addons.length > 1 ? "s" : ""} pour enrichir votre offre</div>
              </div>
            </div>
            <a
              href="/tenant/settings#addons"
              className="text-[11px] px-3 py-1.5 rounded-lg bg-[#f7c935]/10 text-[#f7c935] border border-[#f7c935]/20 hover:bg-[#f7c935]/20 transition-colors flex items-center gap-1 font-medium"
              data-testid="btn-view-addons"
            >
              Explorer <ChevronRight className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
